/**
 * Navigate to an external URL in a way that survives being inside a preview
 * iframe (e.g. the Replit canvas / workspace preview, internal admin tooling
 * that nests the app in an iframe, etc).
 *
 * Many third-party hosted flows — Stripe Connect onboarding, OAuth providers,
 * carrier KYC pages — set `X-Frame-Options: DENY` (or a `frame-ancestors`
 * CSP), so a plain `window.location.href = url` from inside an iframe shows
 * the browser's broken-page icon instead of the destination.
 *
 * Strategy:
 *   1. Open in a new tab — this escapes the iframe AND keeps the user's
 *      current page state so the post-redirect "come back and check status"
 *      flow works seamlessly.
 *   2. If popups are blocked, try navigating the top-level window, which
 *      also escapes any preview iframe.
 *   3. Last resort: same-tab nav. In production this works fine; inside a
 *      framing-denied iframe it will at least fail loudly.
 */
export function openExternalRedirect(url: string): void {
  const popup = window.open(url, "_blank", "noopener,noreferrer");
  if (popup) return;
  try {
    const top = window.top ?? window;
    top.location.href = url;
  } catch {
    window.location.href = url;
  }
}

/**
 * Open a blank tab *synchronously* during a user gesture (a click handler),
 * before any `await`. Browsers consume the user-activation token during an
 * `await`, so a `window.open` issued *after* an async call is treated as a
 * programmatic popup and is blocked. Opening up front preserves the gesture.
 *
 * Returns the tab handle (or `null` if the popup was blocked outright). Note we
 * intentionally do NOT pass `noopener` here: that option forces `window.open`
 * to return `null`, but we need the handle so we can point the tab at the real
 * URL once the async work resolves. We sever `opener` ourselves in
 * `fulfillPendingExternalTab`.
 */
export function openPendingExternalTab(): Window | null {
  try {
    return window.open("about:blank", "_blank");
  } catch {
    return null;
  }
}

/**
 * Point a previously-opened pending tab at the resolved URL. If the tab was
 * never created (popup blocked) or has since been closed, fall back to the
 * standard {@link openExternalRedirect} strategy so behavior is no worse than
 * before.
 */
export function fulfillPendingExternalTab(tab: Window | null, url: string): void {
  if (tab && !tab.closed) {
    try {
      tab.opener = null;
    } catch {
      /* ignore — some browsers make `opener` read-only */
    }
    try {
      tab.location.href = url;
      return;
    } catch {
      /* fall through to the standard redirect strategy */
    }
  }
  openExternalRedirect(url);
}

/**
 * Close a pending tab that we opened optimistically but can no longer use
 * (the API call failed, was rate-limited, or returned no URL). Prevents
 * leaving an orphan `about:blank` tab behind.
 */
export function closePendingExternalTab(tab: Window | null): void {
  if (tab && !tab.closed) {
    try {
      tab.close();
    } catch {
      /* ignore */
    }
  }
}
